/**
 * The "put the phone away" controls.
 *
 * Two toggles that belong together because they answer the same complaint: nobody wants to hold
 * a screen in front of their face for four hundred metres. One buzzes when it starts to matter;
 * the other narrates so you never have to look at all.
 *
 * Both are opt-in and both are off by default, because both need a permission or a pair of
 * earbuds and neither is any use without the user having decided they want it.
 */

import { el } from '../lib/dom';
import {
  ALERT_RANGES,
  alertsAreSupported,
  requestAlertPermission,
  type AlertRange,
} from '../lib/alerts';
import { speechIsSupported } from '../lib/guidance';

export interface HandsFreeOptions {
  /** Null turns the alert off. */
  onAlertRange: (range: AlertRange | null) => void;
  onPocketMode: (on: boolean) => void;
}

export interface HandsFree {
  /** The sheet itself, mounted by the caller. */
  element: HTMLElement;
  /** The button that opens it. */
  toggle: HTMLElement;
  /** Reflect the state the caller actually applied, e.g. after a permission was refused. */
  setAlertRange: (range: AlertRange | null) => void;
  setPocketMode: (on: boolean) => void;
  destroy: () => void;
}

export function createHandsFree(options: HandsFreeOptions): HandsFree {
  let alertRange: AlertRange | null = null;
  let pocket = false;

  const rangeButtons = new Map<AlertRange | 'off', HTMLElement>();
  const pocketButton = el(
    'button',
    { class: 'button button--ghost button--block', type: 'button', 'data-action': 'pocket' },
    'Read it out to me',
  );

  const note = el('p', { class: 'field__hint', role: 'status' });

  const paint = (): void => {
    for (const [value, button] of rangeButtons) {
      const on = value === (alertRange ?? 'off');
      button.classList.toggle('choice--on', on);
      button.setAttribute('aria-pressed', String(on));
    }
    pocketButton.textContent = pocket ? 'Stop reading it out' : 'Read it out to me';
    pocketButton.classList.toggle('button--primary', pocket);
    pocketButton.classList.toggle('button--ghost', !pocket);
  };

  const chooseRange = (range: AlertRange | null): void => {
    if (range === null) {
      alertRange = null;
      options.onAlertRange(null);
      note.textContent = '';
      paint();
      return;
    }

    // Asked straight from the tap, like every other permission in this product.
    void requestAlertPermission().then((granted) => {
      alertRange = range;
      options.onAlertRange(range);
      note.textContent = granted
        ? `You will be buzzed once, at ${range} m.`
        : `Notifications are blocked, so it will buzz at ${range} m without one.`;
      paint();
    });
  };

  const rangeRow = el(
    'div',
    { class: 'choices' },
    ...[
      el('button', { class: 'choice', type: 'button', onclick: () => chooseRange(null) }, 'Off'),
      ...ALERT_RANGES.map((range) =>
        el(
          'button',
          { class: 'choice', type: 'button', onclick: () => chooseRange(range) },
          `${range} m`,
        ),
      ),
    ].map((button, index) => {
      rangeButtons.set(index === 0 ? 'off' : (ALERT_RANGES[index - 1] as AlertRange), button);
      return button;
    }),
  );

  pocketButton.addEventListener('click', () => {
    pocket = !pocket;
    options.onPocketMode(pocket);
    paint();
  });

  const panel = el(
    'div',
    {
      class: 'handsfree',
      hidden: true,
      role: 'dialog',
      'aria-label': 'Hands-free options',
    },
    el('h2', { class: 'roll__title', text: 'Put the phone away' }),
    el(
      'div',
      { class: 'handsfree__body' },
      el(
        'section',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'Buzz me when they are close' }),
        rangeRow,
        note,
      ),
      alertsAreSupported() || speechIsSupported()
        ? null
        : el('p', {
            class: 'field__hint',
            text: 'This browser has neither a voice nor a vibration motor, so neither of these will do much.',
          }),
      el(
        'section',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'Earbuds in, phone in a pocket' }),
        pocketButton,
        el('p', {
          class: 'field__hint',
          text: 'Spoken distance and direction every twenty seconds, and a buzz if you drift off course.',
        }),
      ),
    ),
  );

  const toggle = el(
    'button',
    { class: 'button button--ghost button--small', type: 'button', 'data-action': 'hands-free' },
    'Hands-free',
  );

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  };
  setOpen(false);

  toggle.addEventListener('click', () => setOpen(panel.hidden));
  panel.addEventListener('click', (event) => {
    if (event.target === panel) setOpen(false);
  });

  paint();

  return {
    element: panel,
    toggle,
    setAlertRange: (range) => {
      alertRange = range;
      paint();
    },
    setPocketMode: (on) => {
      pocket = on;
      paint();
    },
    destroy: () => {
      panel.remove();
      toggle.remove();
    },
  };
}
